// Vue
import { mixins } from "vue-class-component";
import { Vue, Component, Prop, Provide, Watch } from "vue-property-decorator";

// Template
import template from "./template.vue";

// Components
import { Icon } from "../icon/Icon";

// Mixins
import { EventBus } from "../../../mixins/event_bus/EventBus";

type SelectedTab = string | string[];

@Component({
  name: "Tabs",
  components: {
    Icon,
  },
  mixins: [template],
})
export class Tabs extends mixins(EventBus) {
  public get themeMap() {
    const themeMap = {
      secondary: 1,
      default: 2,
      grey: 3,
      line: 4,
    };

    return themeMap[this.theme];
  }

  public get tabsSliderVisiblePaddingClass() {
    return this.slider ? `${this.$appPrefix}-tabs--slider` : "";
  }

  public currentValue!: string[] | string;

  public tabsInfo: { [key: string]: { label: string; disabled: boolean; error: boolean } } = {};
  public isScrollLeftActive: boolean = false;
  public isScrollRightActive: boolean = false;

  @Prop([Array, String])
  public value!: any;

  @Provide("tabsTheme")
  @Prop({ default: "default" })
  public theme!: string;

  @Prop({ default: true })
  public slider!: boolean;

  @Prop({ default: true })
  public visible!: boolean;

  @Prop({ default: false })
  public accordion!: boolean;

  @Prop({ default: null })
  public handleActiveTabChangeControl!: ((SelectedTab, Tabs) => Promise<SelectedTab>) | null;

  public $refs!: {
    tab_viewport: HTMLElement;
    tab_menu: HTMLElement;
  };

  @Watch("value")
  public watchValue(value, oldValue) {
    if (this.currentValue.toString() == value.toString()) return;

    if (this.accordion && this.value instanceof Array) {
      this.handleAccordionMultiSelectValueUpdate(value);
    } else if (!this.accordion) {
      this.$nextTick(() => this.handleActiveTabChange(value));
    }
  }

  @Watch("visible")
  public watchVisible(visible, oldVisible) {
    this.initTabMenuPosition(visible);
  }

  public created() {
    this.$on("registerTabInfo", this.registerNameAndLabel);
    this.$on("updateTabDisabledStatus", this.updateTabDisabledStatus);
    this.$on("updateTabErrorStatus", this.updateTabErrorStatus);
    this.$on("updateTabShowHeaderStatus", this.updateTabShowHeaderStatus);
    this.$on("deleteNameAndLabel", this.deleteNameAndLabel);
    this.$on("setTabAsActive", this.handleActiveTabChange);

    if (this.value instanceof Array) {
      this.currentValue = this.value.slice();
    } else {
      this.currentValue = this.value;
    }
  }

  public destroyed() {
    this.$off("registerTabInfo", this.registerNameAndLabel);
    this.$off("updateTabDisabledStatus", this.updateTabDisabledStatus);
    this.$off("updateTabErrorStatus", this.updateTabErrorStatus);
    this.$off("updateTabShowHeaderStatus", this.updateTabShowHeaderStatus);
    this.$off("deleteNameAndLabel", this.deleteNameAndLabel);
    this.$off("setTabAsActive", this.handleActiveTabChange);
    window.removeEventListener("resize", this.handleResize);
  }

  public mounted() {
    this.broadcast("Tab", "activeChanged", [this.value]);

    this.$nextTick(() => this.initTabMenuPosition(this.visible));

    window.addEventListener("resize", this.handleResize);
  }

  public registerNameAndLabel(
    name: any,
    label: string = "",
    disabled: boolean = false,
    error: boolean = false,
    showHeader: boolean = true
  ) {
    Vue.set(this.tabsInfo, name, { label, disabled, error, showHeader });
  }

  public updateTabDisabledStatus(name: any, disabled: boolean) {
    Vue.set(this.tabsInfo[name], "disabled", disabled);
  }

  public updateTabErrorStatus(name: any, error: boolean) {
    Vue.set(this.tabsInfo[name], "error", error);
  }

  public updateTabShowHeaderStatus(name: any, showHeader: boolean) {
    Vue.set(this.tabsInfo[name], "showHeader", showHeader);
  }

  public deleteNameAndLabel(name: any) {
    Vue.delete(this.tabsInfo, name);
  }

  public handleResize(e: any) {
    if (!this.visible) return;

    this.initTabMenuPosition();
  }

  public handleActiveTabChange(activeTabName) {
    if (this.handleActiveTabChangeControl) {
      this.handleActiveTabChangeControl(activeTabName, this).then((activeTabNameNew) => {
        if (activeTabNameNew) {
          this.handleActiveTabChangeDefault(activeTabNameNew);
        } else {
          this.handleActiveTabChangeDefault(activeTabName);
        }
      });
    } else {
      this.handleActiveTabChangeDefault(activeTabName);
    }
  }

  public handleActiveTabChangeDefault(activeTabName) {
    if (this.value instanceof Array) {
      let activeTabs = this.value.slice() || [];

      if (activeTabs.includes(activeTabName)) {
        activeTabs.splice(activeTabs.indexOf(activeTabName), 1);
      } else {
        activeTabs.push(activeTabName);
      }

      this.currentValue = activeTabs;
      this.$emit("input", activeTabs);
      this.broadcast("Tab", "activeChanged", [activeTabs]);
    } else {
      if (this.tabsInfo[activeTabName].disabled) {
        return;
      }

      this.currentValue = activeTabName;
      this.$emit("input", activeTabName);
      this.broadcast("Tab", "activeChanged", [activeTabName]);

      const tabNames = Object.keys(this.tabsInfo);

      this.isScrollLeftActive = this.checkIfPrevTab(activeTabName);
      this.isScrollRightActive = this.checkIfNextTab(activeTabName);

      const activeTab = this.$refs[`tab_menu_item_${tabNames[tabNames.indexOf(activeTabName)]}`][0];

      this.moveTabsMenuToTab(activeTab);
    }
  }

  public handleAccordionMultiSelectValueUpdate(value: string[]) {
    this.broadcast("Tab", "activeChanged", [value]);
  }

  public prev() {
    if (!this.isScrollLeftActive) return;

    const tabName = this.getPrevTabName();
    const tabToActivate = this.$refs[`tab_menu_item_${tabName}`][0];

    this.moveTabsMenuToTab(tabToActivate);

    this.isScrollLeftActive = this.checkIfPrevTab(tabName);

    this.isScrollRightActive = true;
    this.currentValue = tabName;
    this.handleActiveTabChange(tabName);
    /* this.$emit("input", tabName);
    this.broadcast("Tab", "activeChanged", [tabName]); */
  }

  public next() {
    if (!this.isScrollRightActive) return;

    const tabName = this.getNextTabName();
    const tabToActivate = this.$refs[`tab_menu_item_${tabName}`][0];

    this.moveTabsMenuToTab(tabToActivate);

    this.isScrollRightActive = this.checkIfNextTab(tabName);

    this.isScrollLeftActive = true;
    this.currentValue = tabName;
    this.handleActiveTabChange(tabName);
    /* this.$emit("input", tabName);
    this.broadcast("Tab", "activeChanged", [tabName]); */
  }

  private initTabMenuPosition(visible: boolean = true) {
    if (this.accordion) return;

    if (visible && this.value && this.value.length > 0 && window.innerWidth >= 768) {
      this.isScrollLeftActive = this.checkIfPrevTab(this.value);
      this.isScrollRightActive = this.checkIfNextTab(this.value);

      const activeTab = this.$refs[`tab_menu_item_${this.value}`][0];

      this.moveTabsMenuToTab(activeTab);
    } else if (visible) {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(0px)`));
    } else {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(-100%)`));
    }
  }

  private checkIfPrevTab(activeTabName: string): boolean {
    const tabNames = Object.keys(this.tabsInfo);

    for (let i = tabNames.indexOf(activeTabName) - 1; i >= 0; i--) {
      if (!this.tabsInfo[tabNames[i]].disabled) {
        return true;
      }
    }

    return false;
  }

  private getPrevTabName(): string {
    const tabNames = Object.keys(this.tabsInfo);

    for (let i = tabNames.indexOf(this.value) - 1; i >= 0; i--) {
      if (!this.tabsInfo[tabNames[i]].disabled) {
        return tabNames[i];
      }
    }

    return "";
  }

  private checkIfNextTab(activeTabName: string): boolean {
    const tabNames = Object.keys(this.tabsInfo);

    for (let i = tabNames.indexOf(activeTabName) + 1; i < tabNames.length; i++) {
      if (!this.tabsInfo[tabNames[i]].disabled) {
        return true;
      }
    }

    return false;
  }

  private getNextTabName(): string {
    const tabNames = Object.keys(this.tabsInfo);

    for (let i = tabNames.indexOf(this.value) + 1; i < tabNames.length; i++) {
      if (!this.tabsInfo[tabNames[i]].disabled) {
        return tabNames[i];
      }
    }

    return "";
  }

  private moveTabsMenuToTab(activeTab: HTMLElement) {
    const viewportWidth = this.$refs.tab_viewport.offsetWidth;
    const menuWidth = this.$refs.tab_menu.offsetWidth;

    const activeTabOffsetLeft = activeTab.offsetLeft;
    const menuMiddle = viewportWidth / 2 - activeTab.offsetWidth / 2;
    const availableSpaceForTranslate = menuWidth - viewportWidth;

    if (viewportWidth > menuWidth) {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(0px)`));
    } else if (activeTabOffsetLeft - menuMiddle < 0) {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(0px)`));
    } else if (activeTabOffsetLeft - menuMiddle < availableSpaceForTranslate) {
      this.$nextTick(
        () =>
          (this.$refs.tab_menu.style.transform = `translateX(-${
            activeTab.offsetLeft - (viewportWidth / 2 - activeTab.offsetWidth / 2)
          }px)`)
      );
    } else if (activeTabOffsetLeft - availableSpaceForTranslate > menuMiddle) {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(-${availableSpaceForTranslate}px)`));
    } else {
      this.$nextTick(() => (this.$refs.tab_menu.style.transform = `translateX(0px)`));
    }
  }
}
